import {
  hasCoverageEligible,
  canHaveExcessUM,
  canAddUmbrellaUMUIM,
  getUmbrellaBIMinimum,
  parseBIPDLimits,
  getUmbrellaLimitBIOccurrence,
  getUmbrellaRequirements,
  canAddUmbrella as canAddUmbrellaShared
} from '@ourbranch/policy-utils';
export {
  hasCoverageEligible,
  canHaveExcessUM,
  canAddUmbrellaUMUIM,
  getUmbrellaBIMinimum,
  parseBIPDLimits,
  getUmbrellaLimitBIOccurrence,
  getUmbrellaRequirements
};
import { FarmRiskType, allowedDirectMonolineCurrentCarriers, earthquakeStates, floodStates } from '@ourbranch/lookups';
import { policyTypes } from '@ourbranch/policy-types';
import { isFarmBasedPolicyType, isLiabilityOnlyRiskSelection } from '@ourbranch/farm-utils';
import differenceInCalendarYears from 'date-fns/differenceInCalendarYears';
import cloneDeep from 'lodash-es/cloneDeep';
import isEmpty from 'lodash-es/isEmpty';
import omit from 'lodash-es/omit';
import pick from 'lodash-es/pick';
import mapValues from 'lodash-es/mapValues';
import isPlainObject from 'lodash-es/isPlainObject';
import { isBillingErrorCode } from './billing-error-codes';
import { CondoType, condoTypesWithEntry, condoTypesWithUnitsInBuilding } from './constants';
import { awsDateFormatter } from './formatters';
import { types } from './sanitize';

// Helper function to recursively remove __typename from objects and nested objects
const cleanInput = (input) => {
  return mapValues(omit(input, ['__typename']), (val) => {
    if (isPlainObject(val)) {
      return cleanInput(omit(val, ['__typename']));
    }
    return val;
  });
};

// map for when we want to resubmit a quote from the offer page
export const mapOfferInputToQuoteRequestInput = (data) => {
  const quote = {
    address: data.correctedAddress.address.trim(),
    city: data.correctedAddress.city.trim(),
    state: data.correctedAddress.state,
    zip: data.correctedAddress.zip.trim(),
    rep: data.rep,
    affinity: data.global.affinity,
    affinityGroupStem: data.global.affinityGroupStem,
    firstName: data.fname.trim(),
    lastName: data.lname.trim(),
    isApartment: !!data.correctedAddress.unit && data.selectedOption.includes('R'),
    unit: data.correctedAddress.unit ? data.correctedAddress.unit.trim() : undefined,
    email: data.email ? data.email.trim() : undefined,
    phone: data.phone ? data.phone.trim() : undefined,
    additionalPhoneNumbers: data.additionalPhoneNumbers ? data.additionalPhoneNumbers : [],
    dateOfBirth: awsDateFormatter(data.drivers.find((d) => d.isPrimary).dateOfBirth),
    leadSource: data.leadSource,
    currentAutoCarrier: data.global.currentAutoCarrier ? data.global.currentAutoCarrier.trim() : null,
    currentAutoLimitBIPD: data?.autoCoverage?.policyLimitBIPD || null,
    continuousAutoCoverageYears:
      data.global.continuousAutoCoverageStartDate && !data.global.mostRecentAutoInsuranceCoverageEndDate
        ? differenceInCalendarYears(new Date(), Date.parse(data.global.continuousAutoCoverageStartDate))
        : 0,
    driversLicenseState: data.drivers.find((d) => d.isPrimary).driversLicenseState,
    driversLicenseNumber: data.drivers.find((d) => d.isPrimary).driversLicenseNumber.trim(),
    // insuranceInPast31Days: !!data.global.currentlyAutoInsured,    mostRecentAutoInsuranceCoverageEndDate
    connectedHome: data.connectedHome,
    global: omit(data.global, [
      'currentAutoAMBEST',
      'currentAutoInsuranceState',
      'hadInforceCVPolicy',
      'isRC1PriorAutoCoverageDefaultsSet',
      'assignedBILimitsFromUserInput',
      'autoPolicyHistoryBegins',
      'autoPolicyHistoryCount',
      'mvrResponseCount',
      'aPlusAutoCount',
      'priorExpirationDate',
      'multiPolicyDiscount',
      'priorCars',
      'priorDrivers',
      'rateControlDate',
      'receivedCVData',
      // Read-only on GlobalDetails, not in GlobalDetailsInput (#35461).
      'apiKeyFirst5',
      '__typename'
    ]),
    fromStaff: true,
    policyType: data.selectedOption,
    forcedMultiPolicyDiscount: data.forcedMultiPolicyDiscount,
    optIntoMarketingCampaigns: data.optIntoMarketingCampaigns
  };
  if (data.priorAddress?.address) {
    quote.priorAddress = omit(data.priorAddress, '__typename');
  }
  if (data.home) {
    quote.home = pick(data.home, [
      'basementSqFt',
      'basementType',
      'constructionType',
      'exteriorWallType',
      'garageCarCapacity',
      'garageType',
      'homeQuality',
      'homeLocation',
      'mortgageDetails',
      'numFamiliesInDwelling',
      'numFireplaces',
      'numFullBathrooms',
      'numHalfBathrooms',
      'numOccupants',
      'numStories',
      'numWoodBurningStoves',
      'ownershipStatus',
      'purchaseDate',
      'purchaseAmount',
      'roofOriginal',
      'roofShape',
      'roofType',
      'roofYear',
      'roofYearChangedManually',
      'roofDeck',
      'roofCover',
      'roofDeckAttachment',
      'roofToWallConnection',
      'openingProtection',
      'roofSWR',
      'sqFt',
      'swimmingPool',
      'typeOfHome',
      'yearBuilt',
      'fireHydrantWithin1000ft',
      'stormShutters',
      'fortifiedHome',
      'windstormMitigationLevel',
      'latitude',
      'longitude'
    ]);
    if (quote.home?.mortgageDetails?.length) {
      quote.home.mortgageDetails = quote.home.mortgageDetails.map((m) => ({
        ...omit(m, '__typename'),
        mortgageHolderAddress: isEmpty(m.mortgageHolderAddress) ? null : omit(m.mortgageHolderAddress, '__typename')
      }));
    }
    if (quote.home.homeLocation) {
      quote.home.homeLocation = omit(quote.home.homeLocation, '__typename');
    }
  }
  if (data.condo) {
    quote.condo = pick(data.condo, [
      'constructionType',
      'condoLocation',
      'hasUnitNumber',
      'condoQuality',
      'mortgageDetails',
      'numFullBathrooms',
      'numHalfBathrooms',
      'numOccupants',
      'ownershipStatus',
      'purchaseDate',
      'purchaseAmount',
      'roofShape',
      'sqFt',
      'typeOfCondo',
      'typeOfEntry',
      'hasBasement',
      'buildingHasMoreThanFiveUnits',
      'yearBuilt',
      'latitude',
      'longitude',
      'county',
      'fireHydrantWithin1000ft',
      'windstormMitigationLevel',
      'floor',
      'weeksRentedOut',
      'canineFactor',
      'territory',
      'numVehicles'
    ]);
    quote.condo.hasUnitNumber = !data.noUnitNumber;
    quote.condo.weeksRentedOut = data.condo.monthsRentedOut ? data.condo.monthsRentedOut * 4 : 0;
    if (!condoTypesWithEntry.includes(data.condo.typeOfCondo)) {
      quote.condo.typeOfEntry = null;
    }
    if (!condoTypesWithUnitsInBuilding.includes(quote.condo.typeOfCondo)) {
      quote.condo.buildingHasMoreThanFiveUnits = quote.condo.typeOfCondo !== CondoType.DETACHED;
    }
    if (quote.condo?.mortgageDetails?.length) {
      quote.condo.mortgageDetails = quote.condo.mortgageDetails.map((m) => ({
        ...omit(m, '__typename'),
        mortgageHolderAddress: isEmpty(m.mortgageHolderAddress) ? null : omit(m.mortgageHolderAddress, '__typename')
      }));
    }
    if (quote.condo.condoLocation) {
      quote.condo.condoLocation = omit(quote.condo.condoLocation, '__typename');
    }
  }
  // A farm-system offer's quote is its locations, risks and entity: without them the resubmit is a
  // farm quote with no dwelling, which the rater hard-rejects 50125 + 50131. Pass them through in the
  // new-quote form's shape; mapRequestQuoteToInputType whitelists them to the input types. #36621
  if (isFarmBasedPolicyType(data.selectedOption) && data.farmLocations) {
    quote.entity = data.entity;
    quote.farmLocations = data.farmLocations;
  }
  if (data?.VIN) {
    quote.VIN = data?.VIN.trim();
  }
  return quote;
};

export const mapRequestQuoteToInputType = (data) => {
  const quote = {
    address: data.address.trim(),
    city: data.city.trim(),
    state: data.state,
    zip: data.zip.trim(),
    rep: data.rep,
    affinity: data.affinity,
    affinityGroupStem: data.affinityGroupStem,
    encryptedData: data.encryptedData,
    fname: data.firstName.trim(),
    lname: data.lastName.trim(),
    isApartment: (data.isApartment || !!data.unit) && data.policyType.includes('R'),
    unit: data.unit ? data.unit.trim() : undefined,
    email: data.email ? data.email.trim() : undefined,
    phone: data.phone ? data.phone.trim() : undefined,
    additionalPhoneNumbers: data.additionalPhoneNumbers ? data.additionalPhoneNumbers : [],
    dateOfBirth: data.dateOfBirth ? awsDateFormatter(data.dateOfBirth) : null,
    leadSource: data.leadSource,
    currentAutoCarrier: data.currentAutoCarrier ? data.currentAutoCarrier.trim() : null,
    currentAutoLimitBIPD: data.currentAutoLimitBIPD || data.policyLimitBIPD || null,
    continuousAutoCoverageYears:
      data.priorCoverageInYears || data.priorCoverageInYears === 0
        ? Number.parseInt(data.priorCoverageInYears, 10)
        : null,
    driversLicenseState: data.driversLicenseState ? data.driversLicenseState : null,
    driversLicenseNumber: data.driversLicenseNumber ? data.driversLicenseNumber.trim() : null,
    insuranceInPast31Days: !!data.insuranceInPast31Days,
    connectedHome: data.connectedHome,
    global: data.global,
    fromStaff: true,
    policyType: data.policyType,
    forcedMultiPolicyDiscount: data.forcedMultiPolicyDiscount,
    optIntoMarketingCampaigns: data.optIntoMarketingCampaigns,
    entity: data.entity?.type ? data.entity : undefined,
    isNewPurchase: !!data.isNewPurchase
  };
  if (data.priorAddress?.address) {
    quote.priorAddress = omit(data.priorAddress, '__typename');
  }
  if (data.overrides) {
    quote.overrides = omit(data.overrides, '__typename');
  }
  if (data.policyType.includes('H') && data.home) {
    quote.home = pick(data.home, [
      'basementSqFt',
      'basementType',
      'constructionType',
      'exteriorWallType',
      'garageCarCapacity',
      'garageType',
      'homeQuality',
      'homeLocation',
      'mortgageDetails',
      'numFamiliesInDwelling',
      'numFireplaces',
      'numFullBathrooms',
      'numHalfBathrooms',
      'numOccupants',
      'numStories',
      'numWoodBurningStoves',
      'ownershipStatus',
      'purchaseDate',
      'purchaseAmount',
      'roofOriginal',
      'roofShape',
      'roofType',
      'roofYear',
      'roofYearChangedManually',
      'roofDeck',
      'roofCover',
      'roofDeckAttachment',
      'roofToWallConnection',
      'openingProtection',
      'roofSWR',
      'sqFt',
      'swimmingPool',
      'typeOfHome',
      'yearBuilt',
      'fireHydrantWithin1000ft',
      'stormShutters',
      'fortifiedHome',
      'windstormMitigationLevel',
      'latitude',
      'longitude'
    ]);
    if (quote.home?.mortgageDetails?.length) {
      quote.home.mortgageDetails = quote.home.mortgageDetails.map((m) => ({
        ...omit(m, '__typename'),
        mortgageHolderAddress: isEmpty(m.mortgageHolderAddress) ? null : omit(m.mortgageHolderAddress, '__typename')
      }));
    }
    if (quote.home.homeLocation) {
      quote.home.homeLocation = omit(quote.home.homeLocation, '__typename');
    }
  }
  if (data.policyType.includes('C') && data.condo) {
    quote.condo = pick(data.condo, [
      'constructionType',
      'condoLocation',
      'hasUnitNumber',
      'condoQuality',
      'mortgageDetails',
      'numFullBathrooms',
      'numHalfBathrooms',
      'numOccupants',
      'ownershipStatus',
      'purchaseDate',
      'purchaseAmount',
      'roofShape',
      'sqFt',
      'typeOfCondo',
      'typeOfEntry',
      'hasBasement',
      'buildingHasMoreThanFiveUnits',
      'yearBuilt',
      'latitude',
      'longitude',
      'county',
      'fireHydrantWithin1000ft',
      'windstormMitigationLevel',
      'floor',
      'weeksRentedOut',
      'canineFactor',
      'territory',
      'numVehicles'
    ]);
    quote.condo.hasUnitNumber = !data.noUnitNumber;
    quote.condo.weeksRentedOut = data.condo.monthsRentedOut ? data.condo.monthsRentedOut * 4 : 0;
    if (!condoTypesWithEntry.includes(data.condo.typeOfCondo)) {
      quote.condo.typeOfEntry = null;
    }
    if (!condoTypesWithUnitsInBuilding.includes(quote.condo.typeOfCondo)) {
      quote.condo.buildingHasMoreThanFiveUnits = quote.condo.typeOfCondo !== CondoType.DETACHED;
    }
    if (quote.condo?.mortgageDetails?.length) {
      quote.condo.mortgageDetails = quote.condo.mortgageDetails.map((m) => ({
        ...omit(m, '__typename'),
        mortgageHolderAddress: isEmpty(m.mortgageHolderAddress) ? null : omit(m.mortgageHolderAddress, '__typename')
      }));
    }
    if (quote.condo.condoLocation) {
      quote.condo.condoLocation = omit(quote.condo.condoLocation, '__typename');
    }
  }
  if (isFarmBasedPolicyType(data.policyType) && data.farmLocations) {
    // EntityDetailsInput.type is required: a farm offer stored without an entity sends none, not {}.
    quote.entity = data.entity?.type ? cleanInput(data.entity) : undefined;
    // Liability-only quoting (#33168): the new-offer form's "Liability" risk-type selection is a
    // form-only sentinel, not a FarmRiskType — it never reaches the wire. Any sentinel-typed risk
    // is stripped below, and its presence sends includeFarmLiability: true so the carrier defaults
    // seed the offer with the liability block toggled on (a zero-risk F offer comes back).
    const hasLiabilityOnlyRiskSelection = data.farmLocations.some((location) =>
      location?.risks?.some((risk) => isLiabilityOnlyRiskSelection(risk?.type))
    );
    quote.includeFarmLiability = hasLiabilityOnlyRiskSelection || undefined;
    quote.farmLocations = data.farmLocations.map((location) => {
      const processedLocation = {
        description: location.description,
        numberOfAcres: location.numberOfAcres,
        address: location.address ? omit(location.address, '__typename') : undefined,
        legalDescription: location.legalDescription ? omit(location.legalDescription, '__typename') : undefined,
        additionalLegalDescription: location.additionalLegalDescription,
        locationDirections: location.locationDirections
      };

      // Don't send the coordinates if they are unknown
      if (processedLocation.legalDescription) {
        if (processedLocation.legalDescription.townshipCoordinates === 'unknown') {
          processedLocation.legalDescription.townshipCoordinates = undefined;
        }
        if (processedLocation.legalDescription.rangeCoordinates === 'unknown') {
          processedLocation.legalDescription.rangeCoordinates = undefined;
        }
      }

      // Process risks for this location. Sentinel-typed "Liability" selections (#33168) are
      // dropped — a location whose only risk was the sentinel is submitted with no risks key.
      const risksToSend = (location.risks || []).filter((risk) => risk && !isLiabilityOnlyRiskSelection(risk.type));
      if (risksToSend.length > 0) {
        processedLocation.risks = risksToSend
          .map((risk) =>
            risk
              ? {
                  id: risk.id,
                  type: risk.type,
                  description: risk.description,
                  adjustmentFactor: risk.adjustmentFactor,
                  building:
                    risk.building &&
                    // #33164: InlandMarine joins the building-less risk types — it is not a dwelling
                    // or structure, so sending an empty building would put a meaningless node on the
                    // offer.
                    ![FarmRiskType.Fo6Scheduled, FarmRiskType.Fo6Unscheduled, FarmRiskType.InlandMarine].includes(
                      risk.type
                    )
                      ? // Whitelist to FarmDwellingDetailsInput fields. A resubmitted offer
                        // carries the server-computed building (replacementCostEstimate,
                        // roofReplacementCost, etc.) which are output-only and otherwise
                        // trigger an "Unknown Block" GraphQL error. (#31484, #31486)
                        types.FarmDwellingDetailsInput(risk.building)
                      : undefined,
                  connectedHome: risk.connectedHome ? cleanInput(risk.connectedHome) : undefined,
                  // Whitelist to DwellingRiskCoverageDetailsInput (how FarmRiskDetailInput.coverages
                  // is typed). Like building, a resubmitted offer's coverages carry fields not on
                  // that input type which would otherwise trigger an "Unknown Block" GraphQL error:
                  // insuranceLevel and isHundredReplacementCost are output-only, and
                  // tenantImprovementLimit — while a valid input on FarmRentersRiskCoverageDetailsInput
                  // — is not part of DwellingRiskCoverageDetailsInput, so it's dropped here too.
                  // (#31484, #31486)
                  coverages: risk.coverages ? types.DwellingRiskCoverageDetailsInput(risk.coverages) : undefined
                }
              : null
          )
          .filter((risk) => risk !== null);
      }

      return processedLocation;
    });
  }
  if (data.VIN) {
    quote.VIN = data.VIN.trim();
  }
  return quote;
};

export const mapRevisedBranchPlusQuoteDetails = (quote) => {
  const revisedQuote = omit(mapRevisedQuoteDetails(quote), [
    'home.homeLocation',
    'homeCoverage.minimumHurricaneDeductible',
    'homeCoverage.minimumHurricaneDeductibleValue'
  ]);

  return {
    ...pick(revisedQuote, [
      'fname',
      'lname',
      'email',
      'includeUmbrella',
      'includeRenters',
      'isHomeOwner',
      'leadSource',
      'rep',
      'selectedOption',
      'noBindHome',
      'noBindAuto',
      'fromStaff',
      'includeEarthquake',
      'includeFlood',
      'getMVRs',
      'monthsAtCurrentAddress'
    ]),
    priorAddress: revisedQuote.priorAddress ? omit(revisedQuote.priorAddress, ['__typename']) : undefined,
    correctedAddress: revisedQuote.correctedAddress ? omit(revisedQuote.correctedAddress, ['__typename']) : undefined,
    auto: revisedQuote.auto,
    drivers: quote.drivers
      ? quote.drivers.map((driver) => ({
          ...pick(driver, [
            'id',
            'isPrimary',
            'isCoApplicant',
            'firstName',
            'lastName',
            'suffix',
            'dateOfBirth',
            'gender',
            'excludeDriver',
            'maritalStatus',
            'highestEducation',
            'driversLicenseState',
            'driversLicenseNumber',
            'ageFirstLicensed',
            'fullTimeStudent',
            'goodStudent',
            'teenDiscount',
            'courseDiscountCOnditionCheck',
            'takenCourse',
            'schoolName',
            'communityDriveParticipation',
            'waivedPIPWL',
            'pipElgible',
            'pipOptOut',
            'pipHasQHC',
            'assignedCar',
            'gotDrivingRecord',
            'clearUDRViolation',
            'needSR22',
            'autoViolationDates'
          ])
        }))
      : undefined,
    home: omit(revisedQuote.home, ['originalBasementSqFt', 'originalSqFt', 'roofReplacementYearMax', 'capeConditions']),
    homeCoverage: revisedQuote.homeCoverage,
    autoCoverage: omit(revisedQuote.autoCoverage, ['skipPDValidation']),
    existingCustomer: revisedQuote.existingCustomer,
    inviter: revisedQuote.inviter,
    connectedHome: revisedQuote.connectedHome,
    earthquakeCoverage: revisedQuote.earthquakeCoverage,
    floodCoverage: revisedQuote.floodCoverage,
    umbrellaCoverage: revisedQuote.umbrellaCoverage,
    trailers: revisedQuote.traileers,
    cars: quote.cars
      ? quote.cars.map((car) => ({
          ...pick(car, [
            'make',
            'model',
            'year',
            'VIN',
            'primaryUse',
            'deductibleComprehensive',
            'deductibleCollision',
            'limitRental',
            'roadsideAssistance',
            'limitACPE'
          ])
        }))
      : undefined,
    global: {
      ...pick(revisedQuote.global, [
        'autoEffectiveDate',
        'homeEffectiveDate',
        'currentlyHomeInsured',
        'manualInventorySubmission',
        'discountInventoryScore',
        'discountInventoryScoreAddedDate',
        'discountDriverScore',
        'discountPaperless',
        'discountBixMutualAffinity',
        'homeownersPaymentType',
        'homeownersPaymentMethod',
        'autoPaymentType',
        'autoPaymentMethod',
        'currentlyAutoInsured',
        'currentAutoCarrier',
        'currentAutoCarrierStartDate',
        'currentAutoCarrierEndDate',
        'currentAutoCarrierPolicyNumber',
        'continuousAutoCoverageStartDate',
        'mostRecentAutoInsuranceCoverageEndDate',
        'priorIndividualBILimit',
        'priorOccurrenceBILimit',
        'breakupWithAuto',
        'currentHomeownersCarrier',
        'currentHomeownersCarrierPolicyNumber',
        'breakupWithHomeowners',
        'personalPropertyProtection',
        'emails',
        'phoneNumbers',
        'mailingAddress',
        'billingDayOfMonth',
        'autoBillingDayOfMonth',
        'homeBillingDayOfMonth',
        'affinity',
        'affinityGroupStem',
        'homeSecurityPartnerCustomerType',
        'employeeDiscount',
        'priorCancelNonPay'
      ])
    }
  };
};

const cleanMortgageDetails = (dwelling) => {
  if (dwelling && Array.isArray(dwelling.mortgageDetails)) {
    dwelling.mortgageDetails = dwelling.mortgageDetails.map((m) =>
      omit(m, ['__typename', 'county', 'mortgageHolderAddress.__typename'])
    );
  }
};

const removeTypename = (data) => {
  if (Array.isArray(data)) {
    return data.map(removeTypename);
  }
  if (data !== null && typeof data === 'object') {
    const cleaned = {};
    for (const [key, value] of Object.entries(data)) {
      if (key !== '__typename') {
        cleaned[key] = removeTypename(value);
      }
    }
    return cleaned;
  }
  return data;
};

export const mapRevisedQuoteDetails = (variables) => {
  const {
    cars,
    trailers,
    drivers,
    nonDrivers,
    removedDrivers,
    home,
    homeCoverage,
    autoCoverage,
    auto,
    rentersCoverage,
    umbrellaCoverage,
    condo,
    condoCoverage,
    global,
    priorAddress,
    includeUmbrella,
    includeRenters,
    includeEarthquake,
    includeFlood,
    earthquakeCoverage,
    floodCoverage,
    isHomeOwner,
    scheduledPersonalProperty,
    leadSource,
    selectedOption,
    existingCustomer,
    fname,
    lname,
    connectedHome,
    fromStaff,
    rep,
    getMVRs,
    inviter,
    email,
    correctedAddress,
    preBindUWCheck,
    overrides,
    branchPlusPolicies,
    forcedMultiPolicyDiscount,
    attachedHomeowners,
    attachedAuto,
    attachedRenters,
    attachedCondo,
    interestedParties,
    lienHolders,
    farmInterestedParties,
    monthsAtCurrentAddress,
    farmLocations,
    farm,
    farmCoverage,
    includeFarmLiability,
    farmLiabilityCoverage,
    entity,
    replacementCostSelection,
    ignoredHomeIncidents
  } = variables;

  // clone to clean mortgageDetails
  const homeCopy = cloneDeep(home);
  if (homeCopy) {
    cleanMortgageDetails(homeCopy);
  }
  if (condo) {
    cleanMortgageDetails(condo);
    if (condo.condoLocation) {
      condo.condoLocation = omit(condo.condoLocation, '__typename');
    }
  }
  if (farm) {
    cleanMortgageDetails(farm);
  }

  const isNonRentersBundle = ['HA', 'HB', 'AB', 'CA'].includes(selectedOption);

  return {
    fname,
    lname,
    email,
    selectedOption,
    fromStaff,
    getMVRs,
    noBindAuto: selectedOption === 'HB',
    noBindHome: selectedOption === 'AB',
    existingCustomer: existingCustomer ? omit(existingCustomer, ['__typename']) : null,
    includeUmbrella: includeUmbrella && isNonRentersBundle,
    includeRenters,
    includeEarthquake,
    includeFlood,
    earthquakeCoverage: earthquakeCoverage ? omit(earthquakeCoverage, ['__typename']) : undefined,
    floodCoverage: floodCoverage ? omit(floodCoverage, ['__typename']) : undefined,
    isHomeOwner,
    leadSource,
    rep,
    forcedMultiPolicyDiscount,
    attachedHomeowners,
    attachedAuto,
    attachedRenters,
    attachedCondo,
    monthsAtCurrentAddress,
    inviter: inviter ? omit(inviter, '__typename') : undefined,
    priorAddress: priorAddress ? omit(priorAddress, '__typename') : undefined,
    connectedHome: connectedHome ? omit(connectedHome, ['__typename']) : null,
    scheduledPersonalProperty:
      scheduledPersonalProperty?.items?.length && scheduledPersonalProperty?.deductible
        ? omit(scheduledPersonalProperty, ['__typename'])
        : null,
    home: homeCopy
      ? omit(homeCopy, [
          'county',
          'replacementCostEstimate',
          'highestEducation',
          'fpc',
          'wildfireHazardScore',
          'wildfireGallagherScore',
          'dtoName',
          'dtbowName',
          'dtbowLabel',
          'originalBasementSqFt',
          '__typename'
        ])
      : undefined,
    homeCoverage: homeCoverage ? omit(homeCoverage, ['__typename']) : undefined,
    auto: auto ? omit(auto, ['__typename']) : undefined,
    autoCoverage: autoCoverage ? omit(autoCoverage, ['__typename']) : undefined,
    condo: condo
      ? omit(condo, [
          'county',
          'replacementCostEstimate',
          'highestEducation',
          'fpc',
          'wildfireHazardScore',
          'wildfireGallagherScore',
          'dtoName',
          'dtbowName',
          'dtbowLabel',
          '__typename'
        ])
      : undefined,
    condoCoverage: condoCoverage ? omit(condoCoverage, ['__typename']) : undefined,
    rentersCoverage: rentersCoverage ? omit(rentersCoverage, ['__typename', 'rentersLocation.__typename']) : undefined,
    entity: entity ? removeTypename(entity) : undefined,
    // Whitelist farm locations to the LocationDetailInput schema (which recurses into
    // each risk's building/coverages). Every current caller already pre-sanitizes via
    // types.QuoteDetailsInput, but doing it here too means a future caller that passes a
    // raw, server-rated offer can't reintroduce the "Unknown Block" GraphQL error from
    // output-only dwelling fields. (#31484, #31486)
    farmLocations: farmLocations ? types['[LocationDetailInput]'](farmLocations) : undefined,
    farm: farm ? omit(farm, ['__typename']) : undefined,
    farmCoverage: farmCoverage ? omit(farmCoverage, ['__typename']) : undefined,
    includeFarmLiability,
    farmLiabilityCoverage: farmLiabilityCoverage ? removeTypename(farmLiabilityCoverage) : undefined,
    cars: cars
      ? cars.map((car) =>
          omit(car, ['__typename', 'bodyType', 'year', 'model', 'make', 'garageLocation.__typename', 'addDate'])
        )
      : undefined,
    // Unlike cars, a trailer's make/model are staff-entered (no VIN decode re-derives them), so they
    // must survive the save (#36346).
    trailers: trailers
      ? trailers.map((trailer) => omit(trailer, ['__typename', 'bodyType', 'garageLocation.__typename', 'addDate']))
      : undefined,
    drivers: drivers
      ? drivers.map((driver) =>
          omit(driver, [
            '__typename',
            'firstName',
            'lastName',
            // middleName and suffix are deliberately NOT omitted: the backend merge treats an
            // explicit '' on them as a clear (CLEARABLE_PERSON_FIELDS in @ourbranch/rater-helpers),
            // so they must reach it — omitting them made clears silently not stick (#34502, #33085).
            'age',
            'schoolLocation.__typename',
            'schoolZip',
            'autoViolationsForUW',
            'autoViolations',
            'autoViolationDates',
            'addDate',
            'ratableIncidentCount'
          ])
        )
      : undefined,
    removedDrivers: removedDrivers || undefined,
    // #36871: the home copies an underwriter chose not to count; the server stamps who and when
    ignoredHomeIncidents: ignoredHomeIncidents
      ? ignoredHomeIncidents.map(({ date, class: incidentClass }) => ({ date, class: incidentClass }))
      : undefined,
    nonDrivers: nonDrivers ? nonDrivers.map((p) => omit(p, ['__typename'])) : undefined,
    umbrellaCoverage: umbrellaCoverage
      ? omit(
          {
            ...umbrellaCoverage,
            rentalPropertyAddresses: umbrellaCoverage?.rentalPropertyAddresses
              ? umbrellaCoverage.rentalPropertyAddresses.map((x) => omit(x, ['__typename']))
              : null,
            otherPropertyAddresses: umbrellaCoverage?.otherPropertyAddresses
              ? umbrellaCoverage.otherPropertyAddresses.map((x) => omit(x, ['__typename']))
              : null
          },
          ['__typename']
        )
      : undefined,
    global: global
      ? {
          ...omit(global, [
            '__typename',
            'receivedCVData',
            'hadInforceCVPolicy',
            'rateControlDate',
            'priorDrivers',
            'priorCars',
            'preBindUWCheck',
            'isRC1PriorAutoCoverageDefaultsSet',
            // Read-only on GlobalDetails, not in GlobalDetailsInput (#35461).
            'apiKeyFirst5',
            'skipUnderwritingUntil' // Exclude from general mutations - use dedicated mutation instead
          ])
        }
      : undefined,
    correctedAddress,
    preBindUWCheck: preBindUWCheck
      ? {
          ...omit(preBindUWCheck, ['__typename']),
          homeVerification: preBindUWCheck.homeVerification
            ? omit(preBindUWCheck.homeVerification, ['__typename'])
            : undefined,
          autoVerification: preBindUWCheck.autoVerification
            ? omit(preBindUWCheck.autoVerification, ['__typename'])
            : undefined,
          farmVerification: preBindUWCheck.farmVerification
            ? omit(preBindUWCheck.farmVerification, ['__typename'])
            : undefined
        }
      : undefined,
    overrides: omit(overrides, ['__typename']),
    branchPlusPolicies: branchPlusPolicies
      ? branchPlusPolicies.map((policy) => omit(policy, ['__typename']))
      : undefined,
    interestedParties: interestedParties ? interestedParties.map((party) => omit(party, ['__typename'])) : undefined,
    lienHolders: lienHolders ? lienHolders.map((lien) => omit(lien, ['__typename'])) : undefined,
    farmInterestedParties: farmInterestedParties
      ? farmInterestedParties.map((party) => omit(party, ['__typename']))
      : undefined,
    replacementCostSelection
  };
};

export const hasRejection = ({ rejections }) => rejections?.length > 0;

// `${message} ERROR_CODE: ${code}` — appended by the charge-policy bind step so the numeric
// billing code survives Lambda error serialization (the chargePolicy response VTL only forwards
// message + type). Anchored to the end of the string so it can only match that appended suffix.
const BILLING_ERROR_CODE_MARKER = /\s*ERROR_CODE:\s*(\d+)\s*$/;

// A payment decline from the charge-policy bind step: recover the numeric billing code from the
// ERROR_CODE marker and strip the marker from the member-facing decline reason (e.g. "Your card
// was declined. Please try a different card or contact your bank."). Returns null for anything
// that isn't a marker-suffixed billing decline, so other error shapes keep their existing paths
// (notably the EXISTING_MEMBER pre-bind rejection, whose marker carries a non-numeric code).
const formattedBillingDecline = (message) => {
  if (typeof message !== 'string') {
    return null;
  }
  const markerMatch = message.match(BILLING_ERROR_CODE_MARKER);
  if (!markerMatch || !isBillingErrorCode(markerMatch[1])) {
    return null;
  }
  return { message: message.replace(BILLING_ERROR_CODE_MARKER, ''), code: Number(markerMatch[1]) };
};

export const formattedError = (err) => {
  const lambdaUnhandledMessage = '{}';
  const message = err?.graphQLErrors?.[0]?.message || err?.message;
  const billingDecline = formattedBillingDecline(message);
  if (billingDecline) {
    return billingDecline;
  }
  try {
    if (!message) {
      const match = err.match(/^(?<message>.*?)\[(?<code>[0-9]+?)\](\[(?<metadata>.*?)\])?/);
      if (match?.groups) {
        return {
          message: match.groups.message,
          code: match.groups.code,
          metadata: match.groups.metadata
        };
      }
      return { message: err, code: 0 };
    }
    if (message === lambdaUnhandledMessage) {
      return { message: 'Unhandled Lambda Error', code: 0 };
    }

    // a json stringified error
    const parsed = JSON.parse(message);
    if (parsed) {
      if (parsed.message?.includes('Stripe')) {
        return {
          code: parsed.jse_cause?.code || 0,
          message: `Error from Stripe: ${parsed.message}`
        };
      }
      if (parsed.type?.includes('Stripe')) {
        return {
          code: 0,
          message: `Error from Stripe: ${parsed.raw?.message || ''} ${parsed.raw?.decline_code || ''}`
        };
      }
      if (!parsed.code && parsed.errorMessage) {
        const parsedMessage = JSON.parse(parsed.errorMessage);
        return parsedMessage;
      }
      return parsed;
    }

    // return raw message if cannot be parsed
    return { message, code: 0 };
  } catch {
    if (message) {
      return { message, code: 0 };
    }
    return { message: err, code: 0 };
  }
};
export const checkIfUnauthorizedAndPushToSearchPage = (error, history) => {
  if (error.message.includes('Not Authorized')) {
    console.log(error.message, 'pushing back to home');
    history.push('/search/offers');
  }
};

export const isUmbrellaBlockedByPriorCarrier = ({ global, renewalVariables, includeUmbrella }) =>
  // Only disable umbrella because of prior carrier in production
  // also skip this check for kurt muldrew-clean (!== 'undefined')
  process.env.NODE_ENV !== 'development' &&
  !!global?.currentAutoAMBEST &&
  global.currentAutoAMBEST !== 'undefined' &&
  !allowedDirectMonolineCurrentCarriers.includes(global.currentAutoAMBEST) &&
  !!global.currentAutoCarrier &&
  !renewalVariables?.allowUmbrella &&
  global.currentAutoCarrier !== 'BRANCH INSURANCE EXC' &&
  !includeUmbrella; // if this policy already has umbrella, allow it here because this is a pre-bind check

// picks the option for the umbrella "fix Auto BI" link: lowest qualifying BI without
// downgrading the member's PD portion or moving them off CSL
export const getQualifyingBIPDOption = (options, currentPolicyLimitBIPD, renewalVariables) => {
  const current = parseBIPDLimits(currentPolicyLimitBIPD);
  const biMinimum = getUmbrellaBIMinimum(renewalVariables);
  const qualifying = (options || [])
    .map((option) => ({ option, limits: parseBIPDLimits(option.id) }))
    .filter(({ limits }) => limits && limits.limitBI >= biMinimum);
  if (!qualifying.length) {
    return undefined;
  }
  if (current?.isCSL) {
    return (qualifying.find(({ limits }) => limits.isCSL) ?? qualifying[0]).option;
  }
  const preservingPD = qualifying
    .filter(({ limits }) => limits.limitPD !== null && limits.limitPD >= (current?.limitPD ?? 0))
    .sort((a, b) => a.limits.limitPD - b.limits.limitPD || a.limits.limitBI - b.limits.limitBI);
  return (preservingPD[0] ?? qualifying[0]).option;
};

// umbrellaCoverage.limitBIOccurrence for an auto BI limit, as cd-home-rater derives it: a CSL uses its own
// number, a split limit is '300' under $500K per person and '500' otherwise
// home/condo MPD with auto and umbrella, keeping renters and second home (getHomeownersMultiPolicyDiscount format)
export const getUmbrellaEstimateMultiPolicyDiscount = (multiPolicyDiscount) =>
  `A${multiPolicyDiscount?.includes('R') ? 'R' : ''}U${multiPolicyDiscount?.includes('S') ? 'S' : ''}`;

export const canAddUmbrella = (inputs) => canAddUmbrellaShared(inputs) && !isUmbrellaBlockedByPriorCarrier(inputs);

export const checkAdditionalCoverages = (offer, policyType, isAgency = false) => {
  if (!offer || !policyType) {
    return { canAddEarthquake: false, canAddFlood: false };
  }
  const hasHome = policyType === policyTypes.Home || policyType === policyTypes.HABundle;
  const state = offer?.quote?.correctedAddress?.state;
  const canAddEarthquake = hasHome && !isAgency && earthquakeStates.includes(state);
  const canAddFlood = hasHome && !isAgency && floodStates.includes(state);
  return { canAddEarthquake, canAddFlood };
};
